import { IClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
import {
  ClubProgramStatus,
  IClubProgram,
  IUpsertClubProgramDto,
} from '@/interfaces/IClubProgram';

/**
 * Working copy edited across the wizard steps. Numeric fields are kept as
 * `number | null` (inputs may be cleared mid-edit); the modal normalizes and
 * validates before building the upsert DTO.
 */
export interface IClubProgramDraft {
  name: string;
  status: ClubProgramStatus;
  pointsEnabled: boolean;
  earnUnitAmount: number | null;
  earnPoints: number | null;
  redeemPointsCost: number | null;
  redeemValue: number | null;
  minRedeemPoints: number | null;
  maxRedeemPercent: number | null;
  pointsExpiryMonths: number | null;
  welcomePoints: number | null;
  balanceEnabled: boolean;
  topUpPlans: Array<{ amount: number | null; bonus: number | null }>;
  minTopUp: number | null;
  maxMemberBalance: number | null;
  branchIds: number[] | null;
  termsText: string;
}

/** Full upsert DTO from a persisted program (single-field updates like pause). */
export function programToUpsertDto(
  program: IClubProgram
): IUpsertClubProgramDto {
  return {
    name: program.name,
    status: program.status,
    pointsEnabled: program.pointsEnabled,
    earnUnitAmount: +program.earnUnitAmount,
    earnPoints: program.earnPoints,
    redeemPointsCost: program.redeemPointsCost,
    redeemValue: +program.redeemValue,
    minRedeemPoints: program.minRedeemPoints,
    maxRedeemPercent: program.maxRedeemPercent,
    pointsExpiryMonths: program.pointsExpiryMonths,
    welcomePoints: program.welcomePoints,
    balanceEnabled: program.balanceEnabled,
    topUpPlans: program.topUpPlans,
    minTopUp: +program.minTopUp,
    maxMemberBalance:
      program.maxMemberBalance != null ? +program.maxMemberBalance : null,
    branchIds: program.branchIds,
    termsText: program.termsText,
  };
}

export function createClubProgramDraft(
  program: IClubProgram | null,
  defaultName: string,
  defaults: IClubCurrencyDefaults
): IClubProgramDraft {
  if (!program) {
    return {
      name: defaultName,
      status: ClubProgramStatus.Active,
      pointsEnabled: true,
      earnUnitAmount: defaults.earnUnitAmount,
      earnPoints: defaults.earnPoints,
      redeemPointsCost: defaults.redeemPointsCost,
      redeemValue: defaults.redeemValue,
      minRedeemPoints: null,
      maxRedeemPercent: null,
      pointsExpiryMonths: null,
      welcomePoints: null,
      balanceEnabled: false,
      topUpPlans: [],
      minTopUp: null,
      maxMemberBalance: null,
      branchIds: null,
      termsText: '',
    };
  }

  return {
    name: program.name,
    status: program.status,
    pointsEnabled: program.pointsEnabled,
    earnUnitAmount: +program.earnUnitAmount || null,
    earnPoints: program.earnPoints || null,
    redeemPointsCost: program.redeemPointsCost || null,
    redeemValue: +program.redeemValue || null,
    minRedeemPoints: program.minRedeemPoints || null,
    maxRedeemPercent:
      program.maxRedeemPercent && program.maxRedeemPercent !== 100
        ? program.maxRedeemPercent
        : null,
    pointsExpiryMonths: program.pointsExpiryMonths ?? null,
    welcomePoints: program.welcomePoints || null,
    balanceEnabled: program.balanceEnabled,
    topUpPlans: (program.topUpPlans ?? []).map((plan) => ({
      amount: plan.amount,
      bonus: plan.bonus,
    })),
    minTopUp: +program.minTopUp || null,
    maxMemberBalance:
      program.maxMemberBalance != null ? +program.maxMemberBalance : null,
    branchIds: program.branchIds ? [...program.branchIds] : null,
    termsText: program.termsText ?? '',
  };
}
